import { HOC_MDXWrapper } from 'nextra/setup-page'
import { pageMap } from '/ui/docs-site/.next/static/chunks/nextra-page-map-.mjs'
/*@jsxRuntime automatic*/
/*@jsxImportSource react*/
/*-- dri: Lynette Miles --*/
import {useMDXComponents as _provideComponents} from "nextra/mdx";
const title = "Create monitors to generate alerts and notifications";
const frontMatter = {
  "title": "Create monitors to generate alerts and notifications",
  "description": "View and create monitors for alerting."
};
import {Alert, CollectionIcon, Icon, ServiceIcon, Tabs} from '@/components';
import {mdiAlarmSnooze, mdiAlertCircle, mdiAlertDecagram, mdiBell, mdiCheckCircle, mdiClockOutline, mdiContentCopy, mdiDelete, mdiDotsVertical, mdiDownload, mdiFileTreeOutline, mdiPlus, mdiWrench} from '@mdi/js';
import ApiAuthReq, {useTOC as useTOC0} from '@/_partials/api-auth-req.mdx';
import InfoTerraformLockout, {useTOC as useTOC1} from '@/_partials/info-terraformlockout.mdx';
import TerraformDel, {useTOC as useTOC2} from '@/_partials/terraformdel.mdx';
import TerraformPlan, {useTOC as useTOC3} from '@/_partials/terraformplan.mdx';
export function useTOC(props) {
  return [{
    value: "View available monitors",
    id: "view-available-monitors",
    depth: 2
  }, ...useTOC0(), {
    value: "Create a monitor",
    id: "create-a-monitor",
    depth: 2
  }, {
    value: "Prerequisites",
    id: "prerequisites",
    depth: 3
  }, {
    value: "Create monitors",
    id: "create-monitors",
    depth: 3
  }, ...useTOC3(), ...useTOC0(), {
    value: "Chronoctl examples",
    id: "chronoctl-examples",
    depth: 4
  }, {
    value: "Terraform examples",
    id: "terraform-examples",
    depth: 4
  }, {
    value: "Edit a monitor",
    id: "edit-a-monitor",
    depth: 2
  }, ...useTOC1(), ...useTOC0(), {
    value: "Use the Code Config tool",
    id: "use-the-code-config-tool",
    depth: 3
  }, {
    value: "Change code representation",
    id: "change-code-representation",
    depth: 4
  }, {
    value: "Override a monitor alert",
    id: "override-a-monitor-alert",
    depth: 3
  }, ...useTOC1(), {
    value: "Delete a monitor",
    id: "delete-a-monitor",
    depth: 2
  }, ...useTOC1(), ...useTOC2(), ...useTOC0(), {
    value: "Use annotations with monitors",
    id: "use-annotations-with-monitors",
    depth: 2
  }];
}
function MDXLayout(props) {
  const {toc = useTOC(props)} = props;
  const _components = {
    a: "a",
    code: "code",
    em: "em",
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    li: "li",
    mark: "mark",
    ol: "ol",
    p: "p",
    pre: "pre",
    span: "span",
    strong: "strong",
    table: "table",
    tbody: "tbody",
    td: "td",
    th: "th",
    thead: "thead",
    tr: "tr",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  if (!Tabs) _missingMdxReference("Tabs", false);
  if (!Tabs.Tab) _missingMdxReference("Tabs.Tab", true);
  return <>{}{"\n"}<_components.h1>{"Create monitors to generate alerts and notifications"}</_components.h1>{"\n"}<_components.p>{"One of the reasons to ingest and store time series data is to know when data meets or\ndoesn’t meet certain criteria. Use Chronosphere Observability Platform alerting to\ngenerate alerts and notifications from data, whether it’s about your system or about\nyour usage of Observability Platform itself. Compare your monitor configurations to\nhistorical data to ensure your thresholds meet your needs."}</_components.p>{"\n"}<_components.h2 id={toc[0].id}>{toc[0].value}</_components.h2>{"\n"}<_components.p>{"Select from the following methods to view and filter monitors."}</_components.p>{"\n"}<_components.p>{"To query and get detailed information about monitors, see\n"}<_components.a href="/investigate/alerts/monitors/monitor-details">{"Monitor details"}</_components.a>{"."}</_components.p>{"\n"}<Tabs><Tabs.Tab label="Web" id="view-available-monitors-cloud"><_components.p>{"To display a list of defined monitors, in the navigation menu select\n"}<_components.strong><Icon path={mdiBell} />{" Alerts "}<span aria-label="and then">{">"}</span>{" Monitors"}</_components.strong>{"."}</_components.p><_components.p>{"The list of monitors displays the status for each monitor next to its title:"}</_components.p><_components.table><_components.thead><_components.tr><_components.th>{"Icon"}</_components.th><_components.th>{"Description"}</_components.th></_components.tr></_components.thead><_components.tbody><_components.tr><_components.td><Icon path={mdiAlertDecagram} style={{
    color: 'rgb(238, 108, 108)'
  }} /></_components.td><_components.td>{"Currently alerting monitor that exceeds the defined critical conditions."}</_components.td></_components.tr><_components.tr><_components.td><Icon path={mdiAlertCircle} style={{
    color: 'rgb(255, 178, 73)'
  }} /></_components.td><_components.td>{"Currently alerting monitor that exceeds the defined warning conditions."}</_components.td></_components.tr><_components.tr><_components.td><Icon path={mdiAlarmSnooze} style={{
    color: 'rgb(193, 166, 248)'
  }} /></_components.td><_components.td>{"Monitor that’s currently muted by an active muting rule."}</_components.td></_components.tr><_components.tr><_components.td><Icon path={mdiCheckCircle} style={{
    color: 'rgb(89, 204, 141)'
  }} /></_components.td><_components.td>{"Passing monitor that’s not generating alerts."}</_components.td></_components.tr></_components.tbody></_components.table><_components.p>{"Use the following methods to filter your monitors:"}</_components.p><_components.ul>{"\n"}<_components.li>{"Using the "}<_components.strong>{"Search monitors"}</_components.strong>{" search box (an "}<_components.strong>{"OR"}</_components.strong>{" filter)."}</_components.li>{"\n"}<_components.li>{"By team, using the "}<_components.strong>{"Select a team"}</_components.strong>{" dropdown."}</_components.li>{"\n"}<_components.li>{"By owner, using the "}<_components.strong>{"Select an owner"}</_components.strong>{" dropdown. The "}<CollectionIcon />{" icon\nindicates the monitor is part of a "}<_components.a href="/administer/collections">{"collection"}</_components.a>{". The\n"}<ServiceIcon />{" icon indicates this monitor is part of a service."}</_components.li>{"\n"}<_components.li>{"By notification policy, using the "}<_components.strong>{"Select a notification policy"}</_components.strong>{" dropdown."}</_components.li>{"\n"}<_components.li>{"By error status."}</_components.li>{"\n"}</_components.ul><_components.p>{"Monitors with defined signals display the "}<Icon path={mdiFileTreeOutline} />{" file\ntree icon. To view the signals from a displayed monitor, click the name of the\nmonitor from the list."}</_components.p><_components.p>{"From a monitor’s detail page, click the name of a signal from the "}<_components.strong>{"Signals"}</_components.strong>{" section\nto filter the query results to alerts only from that signal."}</_components.p><_components.p>{"To search for a specific monitor:"}</_components.p><_components.ol>{"\n"}<_components.li>{"Click the search bar to focus on it, or use the keyboard shortcut\n"}<kbd>{"Control+K"}</kbd>{" ("}<kbd>{"Command+K"}</kbd>{" on macOS)."}</_components.li>{"\n"}<_components.li>{"Begin typing any part of the monitor’s name."}</_components.li>{"\n"}<_components.li>{"Optional: Click the filters for all other listed resource types at the top of the\nsearch results to remove them and display only monitor."}</_components.li>{"\n"}<_components.li>{"Click the search result you’re interested in, or use the arrow keys to select it\nand press enter, to go to that monitor."}</_components.li>{"\n"}</_components.ol></Tabs.Tab><Tabs.Tab label="Chronoctl" id="view-available-monitors-chronoctl"><_components.p>{"To use "}<_components.a href="/tooling/chronoctl">{"Chronoctl"}</_components.a>{" to return all monitors, use the\n"}<_components.code>{"chronoctl monitors list"}</_components.code>{" command:"}</_components.p><_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"chronoctl"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" monitors"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" list"}</_components.span></_components.span></_components.code></_components.pre><_components.p>{"To filter for a specific monitor, add the "}<_components.code>{"slugs"}</_components.code>{" argument to the command:"}</_components.p><_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"chronoctl"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" monitors"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" list"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" --slugs"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" "}</_components.span><_components.mark data-highlighted-chars=""><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{"SLUG"}</_components.span></_components.mark></_components.span></_components.code></_components.pre><_components.p>{"Replace "}<_components.em><_components.code>{"SLUG"}</_components.code></_components.em>{" with the slug for the monitor you want to\ndisplay."}</_components.p><_components.p><_components.a href="#use-the-code-config-tool">{"Use the Code Config tool"}</_components.a>{" in Observability Platform to\nview the monitor’s Chronoctl YAML representation."}</_components.p></Tabs.Tab><Tabs.Tab label="Terraform" id="view-available-monitors-terraform"><_components.p><_components.a href="#use-the-code-config-tool">{"Use the Code Config tool"}</_components.a>{" in Observability Platform to\nview a monitor’s Terraform representation."}</_components.p></Tabs.Tab><Tabs.Tab label="API" id="view-available-monitors-api"><_components.p>{"To complete this action with the Chronosphere API, use the\n"}<_components.a href="/tooling/api-info/definition/operations/ListMonitors"><_components.code>{"ListMonitors"}</_components.code></_components.a>{" endpoint."}</_components.p><ApiAuthReq /></Tabs.Tab></Tabs>{"\n"}<_components.h2 id={toc[1].id}>{toc[1].value}</_components.h2>{"\n"}<_components.p>{"Most monitors alert when a value matches a specific condition, such as when an error\ncondition defined by the query lasts longer than one minute."}</_components.p>{"\n"}<_components.p>{"You can also choose to alert when a value doesn’t exist, such as when a host stops\nsending metrics and is likely unavailable. This condition triggers only if the\n"}<_components.em>{"entire"}</_components.em>{" monitor query returns no results. For example, to alert on missing or no\ndata, add a "}<_components.code>{"NOT_EXISTS"}</_components.code>{" series condition in the "}<_components.code>{"series_conditions"}</_components.code>{" section of the\nmonitor definition:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="yaml" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"series_conditions"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  defaults"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    critical"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"      conditions"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"        -"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" op"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" NOT_EXISTS"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"          sustain"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" 60s"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"To receive alerts when a host stops sending metrics, create a separate monitor for\neach host and scope the monitor query to that host."}</_components.p>{"\n"}<_components.h3 id={toc[2].id}>{toc[2].value}</_components.h3>{"\n"}<_components.p>{"Before creating a monitor, complete the following tasks:"}</_components.p>{"\n"}<_components.ol>{"\n"}<_components.li>{"Create a "}<_components.a href="/investigate/alerts/notifications/notifiers">{"notifier"}</_components.a>{" to define where to deliver\nalerts and who to notify."}</_components.li>{"\n"}<_components.li>{"Create a "}<_components.a href="/investigate/alerts/notifications/policies">{"notification policy"}</_components.a>{" to determine how to\nroute notifications to notifiers based on signals that trigger from your monitor.\nYou select the notifier you created for the critical or warning conditions on the\nnotification policy."}</_components.li>{"\n"}</_components.ol>{"\n"}<_components.h3 id={toc[3].id}>{toc[3].value}</_components.h3>{"\n"}<_components.p>{"After completing the "}<_components.a href="#prerequisites">{"prerequisite tasks"}</_components.a>{", use any of the following\nmethods to create a new monitor."}</_components.p>{"\n"}<_components.p>{"When creating or editing a monitor in Observability Platform, you can simulate and\ntest alerts to see how an alert would have performed against historical data. Use\nbacktesting to review how your alert would have performed if it had been defined in\nthe past."}</_components.p>{"\n"}<_components.p>{"Chronosphere recommends a query interval minimum of at least 15 seconds. There can be\na ten second delay between an alert trigger and the notifier activation."}</_components.p>{"\n"}<_components.p>{"You can create a monitor using one of the following procedures, or you can\n"}<_components.a href="/investigate/alerts/monitors/monitor-details#duplicate-a-monitor">{"duplicate an existing monitor"}</_components.a>{"."}</_components.p>{"\n"}<Tabs><Tabs.Tab label="Web" id="create-a-monitor-cloud"><_components.p>{"To add a new monitor:"}</_components.p><_components.ol>{"\n"}<_components.li>{"\n"}<_components.p>{"In the navigation menu select one of these locations:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li><_components.strong><Icon path={mdiBell} />{" Alerts "}<span aria-label="and then">{">"}</span>{" Monitors"}</_components.strong>{"."}</_components.li>{"\n"}<_components.li><_components.strong><Icon path={mdiWrench} />{" Platform "}<span aria-label="and then">{">"}</span>{" Collections"}</_components.strong>{",\nand then select the collection you want to create a monitor for. This can be a\nstandard collection or a "}<_components.a href="/observe/services">{"service"}</_components.a>{"."}</_components.li>{"\n"}</_components.ul>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Create the monitor:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"From the "}<_components.strong>{"Monitors"}</_components.strong>{" page, click "}<_components.strong>{"Create monitor"}</_components.strong>{". You can also choose\n"}<_components.strong>{"Duplicate monitor"}</_components.strong>{" to copy an existing monitor."}</_components.li>{"\n"}<_components.li>{"From the "}<_components.strong>{"Collections"}</_components.strong>{" page, in the "}<_components.strong>{"Monitors"}</_components.strong>{" panel, click "}<_components.strong>{"+ Add"}</_components.strong>{"."}</_components.li>{"\n"}</_components.ul>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Enter the information for the monitor based on its "}<_components.a href="/investigate/alerts/monitors/data-model">{"data model"}</_components.a>{"."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Select an "}<_components.strong>{"Owner"}</_components.strong>{" to organize and filter your monitor. You can select a\ncollection or a service."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Enter a "}<_components.strong>{"Monitor Name"}</_components.strong>{"."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Choose a "}<_components.strong>{"Notification Policy"}</_components.strong>{" to determine which notification policy to use\nat a particular alert severity."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Enter "}<_components.strong>{"Labels"}</_components.strong>{" as key/value pairs to categorize and filter monitors."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"In the "}<_components.strong>{"Query"}</_components.strong>{" section, choose the type of query you want to enter:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Prometheus"}</_components.strong>{": Enter a valid Prometheus query. Click "}<_components.strong>{"Edit in Query Builder"}</_components.strong>{"\nto open your query in the "}<_components.a href="/investigate/querying/metrics/builder">{"Query Builder"}</_components.a>{", where\nyou can construct, optimize, and debug your query before saving it. After\nmodifying your query, click "}<_components.strong>{"Done"}</_components.strong>{" to return to the "}<_components.strong>{"Add Monitor"}</_components.strong>{" page."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Graphite"}</_components.strong>{": Enter a valid Graphite query."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p><_components.strong>{"Logs"}</_components.strong>{": Enter a valid log query, which must include the\n"}<_components.a href="/investigate/querying/query-logs/query-syntax#make-series"><_components.code>{"make-series"}</_components.code></_components.a>{"\noperator with a specified "}<_components.code>{"step"}</_components.code>{" size to return data. This operator uses the\n"}<_components.a href="/investigate/querying/query-logs/query-syntax#count"><_components.code>{"count()"}</_components.code></_components.a>{" function by\ndefault, but you can specify different operators instead."}</_components.p>{"\n"}<_components.p>{"For example, the following query creates a time chart that includes the average for\n"}<_components.code>{"latencyInSeconds"}</_components.code>{". The "}<_components.code>{"step"}</_components.code>{" parameter defines the time step for each bucket in\n"}<_components.a href="https://prometheus.io/docs/prometheus/latest/querying/basics/#time-durations">{"Prometheus time duration format"}</_components.a>{":"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="sql" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"severity "}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{"="}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"WARNING\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"| make"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{"-"}</_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"series "}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"avg"}</_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"(latencyInSeconds) step 15m "}</_components.span><_components.span style={{
    "--shiki-dark": "#C6A0F6",
    "--shiki-light": "#D32F2F"
  }}>{"by"}</_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{" severity, "}</_components.span><_components.span style={{
    "--shiki-dark": "#C6A0F6",
    "--shiki-light": "#D32F2F"
  }}>{"service"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}</_components.ul>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Click "}<_components.strong>{"Check Query"}</_components.strong>{" to validate your query and preview query results."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Click "}<_components.strong>{"Open in Explorer"}</_components.strong>{" to open your query in\n"}<_components.a href="/investigate/querying/metrics/explorer">{"Metrics Explorer"}</_components.a>{", where you can review your query\nfor syntax errors and make necessary changes."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"In the preview, toggle "}<_components.strong>{"Show thresholds"}</_components.strong>{" to display the monitor’s defined\nthresholds."}</_components.p>{"\n"}<_components.p>{"Prometheus users can test monitor conditions by reviewing when a monitor would\nhave triggered based on historical data. The preview reflects existing monitor\nschedules, signal grouping, and overrides."}</_components.p>{"\n"}<_components.p>{"You must define at least one condition for alert simulations to work. Toggle\n"}<_components.strong>{"Simulate alerts"}</_components.strong>{" to backtest your condition against existing data."}</_components.p>{"\n"}<_components.p>{"Use the "}<_components.strong>{"Show alert durations"}</_components.strong>{" toggle to display the time period over which\nthe alert would have been active."}</_components.p>{"\n"}<_components.p>{"If your selected time period has too many alerts, or the entire graph appears to\ndisplay in alerted status, reduce the selected time period. If multiple alerts\nwould have fired simultaneously, only one threshold marker displays. The banner\nshows the correct number of alerts. For example, if a critical and a warning would\nfire at the same time, only one alert displays on the graph. The banner shows two\nalerts would have fired."}</_components.p>{"\n"}<_components.p>{"If your selected query returns too much data, the graph displays an error.\nChronosphere recommends selecting shorter time periods for testing, when possible.\nAlert simulation isn’t available outside the\n"}<_components.a href="/administer/limits-licensing/licensing#ingestion-limits-and-retention-policies">{"raw data retention period"}</_components.a>{"."}</_components.p>{"\n"}<_components.p>{"Select a time range up to the present in the\n"}<_components.strong><Icon path={mdiClockOutline} />{" "}<_components.a href="/navigate/time-ranges">{"time range selector"}</_components.a></_components.strong>{".\nAlert simulations use existing data, and can’t project future alerts."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Optional: Group alerts based on the results returned from the query by\nchoosing an option in the "}<_components.strong>{"Signals"}</_components.strong>{" section."}</_components.p>{"\n"}<Alert severity="info"><_components.p>{"If you select "}<_components.strong>{"per signal (multiple alerts)"}</_components.strong>{" to generate multiple alerts, enter\na label key that differs in name and casing from the label you enter in the\n"}<_components.strong>{"Key"}</_components.strong>{" field in the "}<_components.strong>{"Labels"}</_components.strong>{" section. For example, if you enter "}<_components.code>{"environment"}</_components.code>{"\nin the "}<_components.strong>{"Key"}</_components.strong>{" field, you might use "}<_components.code>{"Environments"}</_components.code>{" as the "}<_components.strong>{"Label Key"}</_components.strong>{" to match\non. "}<_components.a href="/navigate/pinned-scopes">{"Pinned scopes"}</_components.a>{" can be used as a "}<_components.strong>{"Label Key"}</_components.strong>{"."}</_components.p></Alert>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Define a condition and sustain period (duration of time) in the "}<_components.strong>{"Conditions"}</_components.strong>{"\nsection, and assign the resulting alert a severity (warning or critical). In\nthe "}<_components.strong>{"Sustain"}</_components.strong>{" field, enter a value followed by an abbreviated unit such as\n"}<_components.code>{"60s"}</_components.code>{". Valid units are "}<_components.code>{"s"}</_components.code>{" (seconds), "}<_components.code>{"m"}</_components.code>{" (minutes), "}<_components.code>{"h"}</_components.code>{" (hours), or "}<_components.code>{"d"}</_components.code>{"\n(days). The dialog also displays the notifiers associated with the monitor for\nreference."}</_components.p>{"\n"}<Alert severity="info"><_components.p>{"To alert on missing or no data, select "}<_components.strong>{"not exists"}</_components.strong>{" in the "}<_components.strong>{"Alert when value"}</_components.strong>{"\ndropdown."}</_components.p></Alert>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"In the "}<_components.strong>{"Resolve"}</_components.strong>{" field, enter a time period for the resolve window as a\nvalue followed by an abbreviated unit such as "}<_components.code>{"30s"}</_components.code>{". Valid units are "}<_components.code>{"s"}</_components.code>{"\n(seconds), "}<_components.code>{"m"}</_components.code>{" (minutes), "}<_components.code>{"h"}</_components.code>{" (hours), or "}<_components.code>{"d"}</_components.code>{" (days)."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Add notes for the monitor in the "}<_components.strong>{"Annotations"}</_components.strong>{" section, such as runbooks,\nlinks to related dashboards, data links to related traces, and documentation\nlinks."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Click "}<_components.strong>{"Save"}</_components.strong>{"."}</_components.p>{"\n"}</_components.li>{"\n"}</_components.ol></Tabs.Tab><Tabs.Tab label="Chronoctl" id="create-a-monitor-chronoctl"><_components.p>{"To create a monitor with "}<_components.a href="/tooling/chronoctl">{"Chronoctl"}</_components.a>{":"}</_components.p><_components.ol>{"\n"}<_components.li>{"\n"}<_components.p>{"Run the following command to generate a sample monitor configuration you can use\nas a template:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"chronoctl"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" monitors"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" scaffold"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"In the template, "}<_components.code>{"kind: Monitor"}</_components.code>{" defines an individual monitor."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"With a completed definition, submit it with:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"chronoctl"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" monitors"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" create"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" -f"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" "}</_components.span><_components.mark data-highlighted-chars=""><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{"FILE_NAME"}</_components.span></_components.mark></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"Replace "}<_components.em><_components.code>{"FILE_NAME"}</_components.code></_components.em>{" with the name of the YAML definition file you want to use."}</_components.p>{"\n"}</_components.li>{"\n"}</_components.ol><_components.p>{"See the "}<_components.a href="#chronoctl-examples">{"Chronoctl monitor examples"}</_components.a>{" for a completed\nmonitor definition."}</_components.p></Tabs.Tab><Tabs.Tab label="Terraform" id="create-a-monitor-terraform"><TerraformPlan /><_components.p>{"To create a monitor with "}<_components.a href="/tooling/infrastructure/terraform">{"Terraform"}</_components.a>{":"}</_components.p><_components.ol>{"\n"}<_components.li>{"\n"}<_components.p>{"Create or edit a Terraform file and add the definition by using the\n"}<_components.code>{"chronosphere_monitor"}</_components.code>{" type, followed by a name in a resource declaration."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Run this command to apply the changes:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"terraform"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" apply"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}</_components.ol><_components.p>{"See the "}<_components.a href="#terraform-examples">{"Terraform monitor examples"}</_components.a>{" for a completed\nmonitor resource."}</_components.p></Tabs.Tab><Tabs.Tab label="API" id="create-a-monitor-api"><_components.p>{"To complete this action with the Chronosphere API, use the\n"}<_components.a href="/tooling/api-info/definition/operations/CreateMonitor"><_components.code>{"CreateMonitor"}</_components.code></_components.a>{" endpoint."}</_components.p><ApiAuthReq /></Tabs.Tab></Tabs>{"\n"}<_components.h4 id={toc[4].id}>{toc[4].value}</_components.h4>{"\n"}<_components.p>{"Use one of the following examples to understand the monitor structure for a Chronoctl\ndefinition."}</_components.p>{"\n"}<Tabs><Tabs.Tab label="Chronoctl (Prometheus)" id="chronoctl-example-prometheus"><_components.p>{"The following YAML definition consists of one monitor named "}<_components.code>{"Disk Getting Full"}</_components.code>{". The\n"}<_components.code>{"series_conditions"}</_components.code>{" trigger a warning notification when the disk is 80% full for more\nthan 300 seconds, and a critical notification when 90% full for more than 300\nseconds. It groups series into signals based on the "}<_components.code>{"source"}</_components.code>{" and\n"}<_components.code>{"service_environment"}</_components.code>{" label keys."}</_components.p><_components.p>{"The "}<_components.code>{"schedule"}</_components.code>{" section indicates that this monitor runs each week on Mondays from\n7:00 to 10:10 and 15:00 to 22:30, and Thursdays from 21:15 through the end of the\nday. All times are in UTC."}</_components.p><Alert severity="info"><_components.p>{"If you define "}<_components.code>{"label_names"}</_components.code>{" in the "}<_components.code>{"signal_grouping"}</_components.code>{" section, enter a label name that\ndiffers in name and casing from the label you enter in the "}<_components.code>{"labels"}</_components.code>{" section. For\nexample, if you enter "}<_components.code>{"environment"}</_components.code>{" as a key in the "}<_components.code>{"labels"}</_components.code>{" section, you might use\n"}<_components.code>{"Environments"}</_components.code>{" in the "}<_components.code>{"label_names"}</_components.code>{" section."}</_components.p></Alert><_components.pre tabIndex="0" data-language="yaml" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"api_version"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" v1/config"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"kind"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" Monitor"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"spec"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Required name of the monitor. Can be modified after the monitor is created."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    name"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" Disk Getting Full"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # PromQL query. If set, you can't set graphite_query."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    prometheus_query"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" max(disk:last{measurement=\"used_percent\"}) by (source, service_environment, region)"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Annotations are visible in notifications generated by this monitor."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # You can template annotations with labels from notifications."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    annotations"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"        key_1"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"{{ $labels.job }}\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Slug of the collection the monitor belongs to."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    collection_slug"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" loadgen"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Optional setting for configuring how often alerts are evaluated."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Defaults to 60 seconds."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    interval_secs"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 60"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Labels are visible in notifications generated by this monitor,"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # and can be used to route alerts with notification overrides."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    labels"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"        key_1"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" kubernetes_cluster"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Optional notification policy used to route alerts generated by the monitor."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    notification_policy_slug"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" custom-notification-policy"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    schedule"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"        # The timezone of the time ranges."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"        timezone"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" UTC"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"        weekly_schedule"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            monday"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                active"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" ONLY_DURING_RANGES"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                # The time ranges that the monitor is active on this day. Required if"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                # active is set to ONLY_DURING_RANGES."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                ranges"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"                    -"}</_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{" # End time in the in format \"<hour>:<minute>\", such as \"15:30\"."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                      end_hh_mm"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"15:00\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                      # Start time in the in format \"<hour>:<minute>\", such as \"15:30\"."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                      start_hh_mm"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"10:10\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            tuesday"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                active"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" NEVER"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            wednesday"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                active"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" NEVER"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            thursday"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                active"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" ONLY_DURING_RANGES"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                # The time ranges that the monitor is active on this day. Required if"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                ranges"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                # active is set to ONLY_DURING_RANGES."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"                    -"}</_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{" # End time in the in format \"<hour>:<minute>\", such as \"15:30\"."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                      end_hh_mm"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"24:00\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                      # Start time in the in format \"<hour>:<minute>\", such as \"15:30\"."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                      start_hh_mm"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"21:15\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            friday"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                active"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" NEVER"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            saturday"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                active"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" NEVER"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            sunday"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                active"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" NEVER"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Conditions evaluated against each queried series to determine the severity of each series."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    series_conditions"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"        defaults"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            critical"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                # List of conditions to evaluate against a series."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                # Only one condition must match to assign a severity to a signal."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                conditions"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                   # To alert on missing or no data, change the value for `op` to `NOT_EXISTS`."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"                    -"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" op"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" GT"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                    # How long the op operation needs to evaluate for the condition"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                    # to evaluate to true."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                    sustain_secs"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 300"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                    # The value to compare to the metric value using the op operation."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                    value"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 60"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                    # How long the operation needs to evaluate false to resolve"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                    resolve_sustain"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 60"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            warn"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                # List of conditions to evaluate against a series."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                # Only one condition must match to assign a severity to a signal."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                conditions"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"                    -"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" op"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" GT"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                    # How long the op operation needs to evaluate for the condition"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                    # to evaluate to true."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                    sustain_secs"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 300"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                    # The value to compare to the metric value using the op operation."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                    value"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 30"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                    # How long the operation needs to evaluate false to resolve"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                    resolve_sustain"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 60"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Defines how the set of series from the query are split into signals."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    signal_grouping"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"        label_names"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"            -"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" source"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"            -"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" service_environment"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"            # If true, each series will have its own signal and label_names can't be set."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"        signal_per_series"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" false"}</_components.span></_components.span></_components.code></_components.pre></Tabs.Tab><Tabs.Tab label="Chronoctl (logs)" id="chronoctl-example-logs"><_components.p>{"The following YAML definition consists of one monitor named\n"}<_components.code>{"Kubernetes errors in production us-west"}</_components.code>{". The "}<_components.code>{"series_conditions"}</_components.code>{" trigger a warning\nnotification when the disk is 80% full for more than 300 seconds, and a critical\nnotification when 90% full for more than 300 seconds. It groups series into signals\nbased on the "}<_components.code>{"source"}</_components.code>{" and "}<_components.code>{"service_environment"}</_components.code>{" label keys."}</_components.p><_components.p>{"The "}<_components.code>{"schedule"}</_components.code>{" section indicates that this monitor runs each week on Mondays from\n7:00 to 10:10 and 15:00 to 22:30, and Thursdays from 21:15 through the end of the\nday. All times are in UTC."}</_components.p><Alert severity="info"><_components.p>{"If you define "}<_components.code>{"label_names"}</_components.code>{" in the "}<_components.code>{"signal_grouping"}</_components.code>{" section, enter a label name that\ndiffers in name and casing from the label you enter in the "}<_components.code>{"labels"}</_components.code>{" section. For\nexample, if you enter "}<_components.code>{"environment"}</_components.code>{" as a key in the "}<_components.code>{"labels"}</_components.code>{" section, you might use\n"}<_components.code>{"Environments"}</_components.code>{" in the "}<_components.code>{"label_names"}</_components.code>{" section."}</_components.p></Alert><_components.pre tabIndex="0" data-language="yaml" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"api_version"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" v1/config"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"kind"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" Monitor"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"spec"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Required name of the monitor. Can be modified after the monitor is created."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    name"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" Kubernetes errors in production us-west"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Logging query to return data for."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    logging_query"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" severity = \"ERROR\" AND kubernetes.cluster_name = \"production-us-west\" | make-series by service"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Annotations are visible in notifications generated by this monitor."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # You can template annotations with labels from notifications."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    annotations"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"        key_1"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"{{ $labels.job }}\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Slug of the collection the monitor belongs to."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    collection_slug"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" production-team"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Optional setting for configuring how often alerts are evaluated."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Defaults to 60 seconds."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    interval_secs"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 60"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Labels are visible in notifications generated by this monitor,"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # and can be used to route alerts with notification overrides."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    labels"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"        key_1"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" kubernetes_cluster"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Optional notification policy used to route alerts generated by the monitor."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    notification_policy_slug"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" custom-notification-policy"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    schedule"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"        # The timezone of the time ranges."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"        timezone"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" UTC"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"        weekly_schedule"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            monday"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                active"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" ONLY_DURING_RANGES"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                # The time ranges that the monitor is active on this day. Required if"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                # active is set to ONLY_DURING_RANGES."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                ranges"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"                    -"}</_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{" # End time in the in format \"<hour>:<minute>\", such as \"15:30\"."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                      end_hh_mm"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"15:00\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                      # Start time in the in format \"<hour>:<minute>\", such as \"15:30\"."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                      start_hh_mm"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"10:10\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            tuesday"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                active"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" NEVER"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            wednesday"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                active"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" NEVER"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            thursday"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                active"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" ONLY_DURING_RANGES"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                # The time ranges that the monitor is active on this day. Required if"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                ranges"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                # active is set to ONLY_DURING_RANGES."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"                    -"}</_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{" # End time in the in format \"<hour>:<minute>\", such as \"15:30\"."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                      end_hh_mm"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"24:00\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                      # Start time in the in format \"<hour>:<minute>\", such as \"15:30\"."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                      start_hh_mm"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"21:15\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            friday"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                active"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" NEVER"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            saturday"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                active"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" NEVER"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            sunday"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                active"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" NEVER"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Conditions evaluated against each queried series to determine the severity of each series."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    series_conditions"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"        defaults"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            critical"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                # List of conditions to evaluate against a series."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                # Only one condition must match to assign a severity to a signal."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                conditions"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                   # To alert on missing or no data, change the value for `op` to `NOT_EXISTS`."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"                    -"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" op"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" GT"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                    # How long the op operation needs to evaluate for the condition"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                    # to evaluate to true."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                    sustain_secs"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 300"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                    # The value to compare to the metric value using the op operation."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                    value"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 60"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                    # How long the operation needs to evaluate false to resolve"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                    resolve_sustain"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 60"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"            warn"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                # List of conditions to evaluate against a series."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                # Only one condition must match to assign a severity to a signal."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                conditions"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"                    -"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" op"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" GT"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                    # How long the op operation needs to evaluate for the condition"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                    # to evaluate to true."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                    sustain_secs"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 300"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                    # The value to compare to the metric value using the op operation."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                    value"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 30"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"                    # How long the operation needs to evaluate false to resolve"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"                    resolve_sustain"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 60"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Defines how the set of series from the query are split into signals."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    signal_grouping"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"        label_names"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"            -"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" source"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"            -"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" service_environment"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"            # If true, each series will have its own signal and label_names can't be set."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"        signal_per_series"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" false"}</_components.span></_components.span></_components.code></_components.pre></Tabs.Tab></Tabs>{"\n"}<_components.h4 id={toc[5].id}>{toc[5].value}</_components.h4>{"\n"}<_components.p>{"Use one of the following examples to understand the monitor structure for a Terraform\nresource."}</_components.p>{"\n"}<Tabs><Tabs.Tab label="Terraform (Prometheus)" id="terraform-example-prometheus"><_components.p>{"The following Terraform resource creates a monitor that Terraform refers to by\n"}<_components.code>{"infra"}</_components.code>{", and with a human-readable name of "}<_components.code>{"Infra Example monitor"}</_components.code>{"."}</_components.p><_components.p>{"The "}<_components.code>{"schedule"}</_components.code>{" section runs this monitor each week on Mondays from 7:00 to 10:10 and\n15:00 to 22:30, and Thursdays from 21:15 through the end of the day. All times are\nUTC, and Observability Platform won’t run this monitor during the rest of the week."}</_components.p><Alert severity="info"><_components.p>{"If you define "}<_components.code>{"label_names"}</_components.code>{" in the "}<_components.code>{"signal_grouping"}</_components.code>{" section, enter a label name\nthat differs in name and casing from the label you enter in the "}<_components.code>{"labels"}</_components.code>{" section.\nFor example, if you enter "}<_components.code>{"environment"}</_components.code>{" as a key in the "}<_components.code>{"labels"}</_components.code>{" section, you\nmight use "}<_components.code>{"Environments"}</_components.code>{" in the "}<_components.code>{"label_names"}</_components.code>{" section."}</_components.p></Alert><_components.pre tabIndex="0" data-language="ruby" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"resource "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"chronosphere_monitor\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"infra\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  name = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Infra Example monitor\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Reference to the collection the alert belongs to."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  collection_id = chronosphere_collection"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"infra"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"id"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Override the notification policy."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # By default, uses the policy from the collection_id."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  notification_policy_id = chronosphere_collection"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"infra_testing"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"id"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Arbitrary set of labels to assign to the alert."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  labels = "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"    \"priority\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"sev-1\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Arbitrary set of annotations to include in alert notifications."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  annotations = "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"    \"runbook\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"http://default-runbook\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Interval at which to evaluate the monitor, for example 15s, 30s, or 60s."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Defaults to 60s."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  interval = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"30s\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  query "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # PromQL query to evaluate for the alert."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Alternatively, you can use graphite_expr instead."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    prometheus_expr = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"sum (rate(grpc_server_handled_total{grpc_code!=\""}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"OK"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"}[1m])) by (app, grpc_service, grpc_method)\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # The remaining examples are optional signals specifying how to group the"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # series returned from the query."}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # No signal_grouping clause = Per monitor"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # signal_grouping with label_names set = Per signal for labels set"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # signal_grouping with signal_per_series set to true = Per series"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  signal_grouping "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Set of labels names used to split series into signals."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Each unique combination of labels results in its own signal."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    label_names = "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"["}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"app\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"grpc_service\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"]"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # As an alternative to label_names, signal_per_series creates an alert for"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # every resulting series from the query."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # signal_per_series = true"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Container for the conditions determining the severity of each series from the query."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # The highest severity series of a signal determines that signal's severity."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  series_conditions "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Condition assigning a warn threshold for series above a certain threshold."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    condition "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # Severity of the condition, which can be \"warn\" or \"critical\"."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      severity = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"warn\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # Value to compare against each series from the query result."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # For EXISTS or NOT_EXISTS operators, value must be set to zero or may be omitted."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      value = "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"5.0"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # Operator to use when comparing the query result versus the threshold."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # Valid values can be one of GT, LT, LEQ, GEQ, EQ, NEQ, EXISTS, NOT_EXISTS."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      op = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"GT\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # Amount of time the query needs to fail the condition check before"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # an alert is triggered. Must be an integer. Accepts one of s (seconds), m"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # (minutes), or h (hours) as units. Optional."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      sustain = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"240s\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # Amount of time the query needs to no longer fire before resolving. Must be"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # an integer. Accepts one of s (seconds), m (minutes), or h (hours) as units."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      resolve_sustain = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"60s\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    condition "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      severity = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"critical\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      value    = "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"10.0"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      op       = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"GT\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      sustain  = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"120s\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      resolve_sustain = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"60s\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Multiple optional overrides can be defined for different sets of conditions"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # to series with matching labels."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    override "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # One or more matchers for labels on a series."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      label_matcher "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"        # Name of the label"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"        name = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"app\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"        # How to match the label, which can be \"EXACT_MATCHER_TYPE\" or"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"        # \"REGEXP_MATCHER_TYPE\"."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"        type = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"EXACT_MATCHER_TYPE\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"        # Value of the label."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"        value = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"dbmon\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"      }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      condition "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"        severity = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"critical\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"        value    = "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"1.0"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"        op       = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"GT\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"        sustain  = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"60s\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"      }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"# If you define a schedule, Observability Platform evaluates the monitor only during"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"# the specified time ranges. The monitor is inactive during all unspecified"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"# time ranges."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"# If you define an empty schedule, Observability Platform never evaluates the monitor."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  schedule "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Valid values: Any IANA timezone string"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    timezone = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"UTC\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    range "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # Time range for the monitor schedule. Valid values for day can be full"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # day names, such as \"Sunday\" or \"Monday\"."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # Valid time values must be specified in the range of 00:00 to 24:00."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      day   = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Monday\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      start = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"07:00\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      end   = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"10:10\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    range "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      day   = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Monday\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      start = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"15:00\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      end   = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"22:30\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    range "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      day   = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Thursday\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      start = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"21:15\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      end   = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"24:00\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span></_components.code></_components.pre></Tabs.Tab><Tabs.Tab label="Terraform (logs)" id="terraform-example-logs"><_components.p>{"The following Terraform resource creates a monitor that Terraform refers to by\n"}<_components.code>{"k8s_production"}</_components.code>{", and with a human-readable name of\n"}<_components.code>{"Kubernetes errors in production us-west"}</_components.code>{"."}</_components.p><_components.p>{"The "}<_components.code>{"schedule"}</_components.code>{" section runs this monitor each week on Mondays from 7:00 to 10:10 and\n15:00 to 22:30, and Thursdays from 21:15 through the end of the day. All times are\nUTC, and Observability Platform won’t run this monitor during the rest of the week."}</_components.p><Alert severity="info"><_components.p>{"If you define "}<_components.code>{"label_names"}</_components.code>{" in the "}<_components.code>{"signal_grouping"}</_components.code>{" section, enter a label name\nthat differs in name and casing from the label you enter in the "}<_components.code>{"labels"}</_components.code>{" section.\nFor example, if you enter "}<_components.code>{"environment"}</_components.code>{" as a key in the "}<_components.code>{"labels"}</_components.code>{" section, you\nmight use "}<_components.code>{"Environments"}</_components.code>{" in the "}<_components.code>{"label_names"}</_components.code>{" section."}</_components.p></Alert><_components.pre tabIndex="0" data-language="ruby" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"resource "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"chronosphere_monitor\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"k8s_production\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  name = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Kubernetes errors in production us-west\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Reference to the collection the alert belongs to."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  collection_id = chronosphere_collection"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"k8s_production"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"id"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Override the notification policy."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # By default, uses the policy from the collection_id."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  notification_policy_id = chronosphere_collection"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"k8s_testing"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"id"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Arbitrary set of labels to assign to the alert."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  labels = "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"    \"priority\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"sev-1\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Arbitrary set of annotations to include in alert notifications."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  annotations = "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"    \"runbook\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"http://default-runbook\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Interval at which to evaluate the monitor, for example 15s, 30s, or 60s."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Defaults to 60s."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  interval = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"30s\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  query "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Logging query to evaluate."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    logging_query = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"severity='ERROR' AND kubernetes.cluster_name='production-us-west' | make-series by service\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # The remaining examples are optional signals specifying how to group the"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # series returned from the query."}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # No signal_grouping clause = Per monitor"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # signal_grouping with label_names set = Per signal for labels set"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # signal_grouping with signal_per_series set to true = Per series"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  signal_grouping "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Set of labels names used to split series into signals."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Each unique combination of labels results in its own signal."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    label_names = "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"["}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"app\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"grpc_service\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"]"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # As an alternative to label_names, signal_per_series creates an alert for"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # every resulting series from the query."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # signal_per_series = true"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Container for the conditions determining the severity of each series from the query."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # The highest severity series of a signal determines that signal's severity."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  series_conditions "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Condition assigning a warn threshold for series above a certain threshold."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    condition "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # Severity of the condition, which can be \"warn\" or \"critical\"."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      severity = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"warn\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # Value to compare against each series from the query result."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # For EXISTS or NOT_EXISTS operators, value must be set to zero or may be omitted."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      value = "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"5.0"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # Operator to use when comparing the query result versus the threshold."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # Valid values can be one of GT, LT, LEQ, GEQ, EQ, NEQ, EXISTS, NOT_EXISTS."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      op = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"GT\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # Amount of time the query needs to fail the condition check before"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # an alert is triggered. Must be an integer. Accepts one of s (seconds), m"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # (minutes), or h (hours) as units. Optional."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      sustain = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"240s\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # Amount of time the query needs to no longer fire before resolving. Must be"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # an integer. Accepts one of s (seconds), m (minutes), or h (hours) as units."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      resolve_sustain = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"60s\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    condition "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      severity = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"critical\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      value    = "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"10.0"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      op       = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"GT\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      sustain  = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"120s\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      resolve_sustain = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"60s\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Multiple optional overrides can be defined for different sets of conditions"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # to series with matching labels."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    override "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # One or more matchers for labels on a series."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      label_matcher "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"        # Name of the label"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"        name = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"app\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"        # How to match the label, which can be \"EXACT_MATCHER_TYPE\" or"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"        # \"REGEXP_MATCHER_TYPE\"."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"        type = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"EXACT_MATCHER_TYPE\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"        # Value of the label."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"        value = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"dbmon\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"      }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      condition "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"        severity = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"critical\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"        value    = "}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{"1.0"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"        op       = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"GT\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"        sustain  = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"60s\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"      }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"# If you define a schedule, Observability Platform evaluates the monitor only during"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"# the specified time ranges. The monitor is inactive during all unspecified"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"# time ranges."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"# If you define an empty schedule, Observability Platform never evaluates the monitor."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  schedule "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"    # Valid values: Any IANA timezone string"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    timezone = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"UTC\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    range "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # Time range for the monitor schedule. Valid values for day can be full"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # day names, such as \"Sunday\" or \"Monday\"."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"      # Valid time values must be specified in the range of 00:00 to 24:00."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      day   = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Monday\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      start = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"07:00\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      end   = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"10:10\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    range "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      day   = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Monday\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      start = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"15:00\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      end   = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"22:30\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"    range "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      day   = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Thursday\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      start = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"21:15\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"      end   = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"24:00\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span></_components.span></_components.code></_components.pre></Tabs.Tab></Tabs>{"\n"}<_components.h2 id={toc[6].id}>{toc[6].value}</_components.h2>{"\n"}<_components.p>{"Select from the following methods to edit monitors."}</_components.p>{"\n"}<InfoTerraformLockout />{"\n"}<Tabs><Tabs.Tab label="Web" id="edit-a-monitor-cloud"><_components.p>{"To edit a monitor:"}</_components.p><_components.ol>{"\n"}<_components.li>{"In the navigation menu select\n"}<_components.strong><Icon path={mdiBell} />{" Alerts "}<span aria-label="and then">{">"}</span>{" Monitors"}</_components.strong>{"."}</_components.li>{"\n"}<_components.li>{"Click the name of the monitor you want to edit."}</_components.li>{"\n"}<_components.li>{"In the action menu, click the "}<Icon path={mdiDotsVertical} />{"\nthree vertical dots icon and select "}<_components.strong>{"Edit monitor"}</_components.strong>{". This opens a sidebar where\nyou can edit the monitor’s properties."}</_components.li>{"\n"}<_components.li>{"Make your edits, and then click "}<_components.strong>{"Save"}</_components.strong>{". Refer to the\n"}<_components.a href="/investigate/alerts/monitors/data-model">{"monitor data model"}</_components.a>{" for specific definitions."}</_components.li>{"\n"}</_components.ol></Tabs.Tab><Tabs.Tab label="Chronoctl" id="edit-a-monitor-chronoctl"><_components.p>{"To edit a monitor using "}<_components.a href="/tooling/chronoctl">{"Chronoctl"}</_components.a>{":"}</_components.p><_components.ol>{"\n"}<_components.li><_components.a href="#view-available-monitors">{"View the monitor’s Chronoctl YAML"}</_components.a>{"."}</_components.li>{"\n"}<_components.li>{"Modify its properties and apply the changes with the same process as\n"}<_components.a href="#create-a-monitor">{"creating a monitor"}</_components.a>{". Chronoctl updates the monitor’s\nproperties if it has the same slug."}</_components.li>{"\n"}</_components.ol><_components.p>{"You can also use the following process if you already have a definition file:"}</_components.p><_components.ol>{"\n"}<_components.li>{"\n"}<_components.p>{"Update the monitor’s definition file."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Run the following command to submit the changes:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"chronoctl"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" monitors"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" update"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" -f"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" "}</_components.span><_components.mark data-highlighted-chars=""><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{"FILE_NAME"}</_components.span></_components.mark></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"Replace "}<_components.em><_components.code>{"FILE_NAME"}</_components.code></_components.em>{" with the name of the YAML definition file you want to use."}</_components.p>{"\n"}</_components.li>{"\n"}</_components.ol><_components.p>{"You can also use the Code Config tool to view the monitor’s Chronoctl YAML\nrepresentation. For details, see "}<_components.a href="#use-the-code-config-tool">{"Use the Code Config tool"}</_components.a>{"."}</_components.p></Tabs.Tab><Tabs.Tab label="Terraform" id="edit-a-monitor-terraform"><_components.p>{"To edit a monitor using "}<_components.a href="/tooling/infrastructure/terraform">{"Terraform"}</_components.a>{":"}</_components.p><_components.ol>{"\n"}<_components.li>{"\n"}<_components.p>{"Create or edit a Terraform file that updates the resource’s existing properties."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Run this command to apply the changes:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"terraform"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" apply"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}</_components.ol><_components.p>{"You can also use the Code Config tool to view the monitor’s Terraform representation.\nFor details, see "}<_components.a href="#use-the-code-config-tool">{"Use the Code Config tool"}</_components.a>{"."}</_components.p></Tabs.Tab><Tabs.Tab label="API" id="edit-a-monitor-api"><_components.p>{"To complete this action with the Chronosphere API, use the\n"}<_components.a href="/tooling/api-info/definition/operations/UpdateMonitor"><_components.code>{"UpdateMonitor"}</_components.code></_components.a>{" endpoint."}</_components.p><ApiAuthReq /></Tabs.Tab></Tabs>{"\n"}<_components.h3 id={toc[7].id}>{toc[7].value}</_components.h3>{"\n"}<_components.p>{"When adding or editing a monitor, you can click the "}<_components.strong>{"Code Config"}</_components.strong>{" tab to view code\nrepresentations of a monitor for "}<_components.a href="/tooling/infrastructure/terraform">{"Terraform"}</_components.a>{",\n"}<_components.a href="/tooling/chronoctl">{"Chronoctl"}</_components.a>{", and the "}<_components.a href="/tooling/api-info">{"Chronosphere API"}</_components.a>{". The displayed code\nalso responds to changes you make in the "}<_components.strong>{"Visual Editor"}</_components.strong>{" tab."}</_components.p>{"\n"}<_components.p>{"When you modify monitor properties in the "}<_components.strong>{"Visual Editor"}</_components.strong>{" tab, you can click the\n"}<_components.strong>{"Code Config"}</_components.strong>{" tab to immediately see the updated code representations. This ability\nlets you use the "}<_components.strong>{"Visual Editor"}</_components.strong>{" tab to modify monitor properties and view the code representations\nexpressed as Terraform resources, Chronoctl YAML, or API-compatible JSON."}</_components.p>{"\n"}<_components.p>{"Changes you make in the "}<_components.strong>{"Visual Editor"}</_components.strong>{" tab don’t take effect until you click\n"}<_components.strong>{"Save"}</_components.strong>{" or apply the code representations using their corresponding tools."}</_components.p>{"\n"}<Alert severity="info"><_components.p>{"If you manage a monitor using Terraform, you "}<_components.em>{"must"}</_components.em>{" use Terraform to apply any\nchanges."}</_components.p></Alert>{"\n"}<_components.h4 id={toc[8].id}>{toc[8].value}</_components.h4>{"\n"}<_components.p>{"To change the code representation format:"}</_components.p>{"\n"}<_components.ol>{"\n"}<_components.li>{"\n"}<_components.p>{"Click the "}<_components.strong>{"Code Config"}</_components.strong>{" tab."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Click the format dropdown. The dropdown defaults to the format of the tool that\nmanages the resource, such as Terraform."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Select the format you want to use. You can then take several additional actions:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"\n"}<_components.p>{"To copy the viewed code representation to your clipboard, click\n"}<_components.strong><Icon path={mdiContentCopy} />{" Copy"}</_components.strong>{"."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"To save the viewed code representation to a file, click "}<_components.strong><Icon path={mdiDownload} />{" Download"}</_components.strong>{"."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"To view a diff of unsaved changes you’ve made to a monitor, click "}<_components.strong>{"View Diff"}</_components.strong>{".\nThis button is available only if you’ve changed the monitor in the "}<_components.strong>{"Visual Editor"}</_components.strong>{"\ntab but haven’t saved your changes."}</_components.p>{"\n"}<_components.p>{"This Git-style diff of changes replaces the "}<_components.strong>{"Copy"}</_components.strong>{" and "}<_components.strong>{"Download"}</_components.strong>{" buttons\nwith a toggle between "}<_components.strong>{"Unified"}</_components.strong>{" and "}<_components.strong>{"Split"}</_components.strong>{" diff styles, and the "}<_components.strong>{"View Diff"}</_components.strong>{"\nbutton with a "}<_components.strong>{"Hide Diff"}</_components.strong>{" button that returns you to the code representation\nview."}</_components.p>{"\n"}<_components.p>{"You can also view unchanged lines in the context of the diff by clicking\n"}<_components.strong>{"Expand X lines"}</_components.strong>{" inside the diff."}</_components.p>{"\n"}</_components.li>{"\n"}</_components.ul>{"\n"}</_components.li>{"\n"}</_components.ol>{"\n"}<_components.h3 id={toc[9].id}>{toc[9].value}</_components.h3>{"\n"}<_components.p>{"You can override the default conditions that define when an alert triggers for a\nmonitor. This override is similar to\n"}<_components.a href="/investigate/alerts/notifications/policies#override-notification-policy-defaults">{"overriding a notification policy"}</_components.a>{"\nthat routes a notification to a notifier other than the specified default."}</_components.p>{"\n"}<_components.p>{"On a monitor, you can specify a condition override to use a separate threshold for\ncertain series. For example, a monitor might have a default threshold of "}<_components.code>{">100"}</_components.code>{" but\nyou specify an override threshold of "}<_components.code>{">50"}</_components.code>{" where the label key/value pair is\n"}<_components.code>{"cluster=production"}</_components.code>{"."}</_components.p>{"\n"}<_components.p>{"You can specify any label as a matcher for a monitor condition override. If no\noverride matches the defined conditions, Observability Platform applies the default\nconditions. Additionally:"}</_components.p>{"\n"}<_components.ul>{"\n"}<_components.li>{"Overrides must specify at least one matcher, and meet every matcher condition to\napply the override."}</_components.li>{"\n"}<_components.li>{"Observability Platform evaluates overrides in the listed order. When an override\nmatches, the remaining overrides and defaults are ignored."}</_components.li>{"\n"}<_components.li>{"Overrides don’t inherit any properties from the default conditions. For example, if\nthe default policy route specifies "}<_components.code>{"warn"}</_components.code>{" and "}<_components.code>{"critical"}</_components.code>{" notifiers but the override\nspecifies only "}<_components.code>{"critical"}</_components.code>{" notifiers, the notifier doesn’t send "}<_components.code>{"warn"}</_components.code>{"\nnotifications."}</_components.li>{"\n"}</_components.ul>{"\n"}<InfoTerraformLockout />{"\n"}<_components.p>{"To specify a monitor alert override:"}</_components.p>{"\n"}<Tabs><Tabs.Tab label="Web" id="override-monitor-cloud"><_components.ol>{"\n"}<_components.li>{"In the navigation menu select\n"}<_components.strong><Icon path={mdiBell} />{" Alerts "}<span aria-label="and then">{">"}</span>{" Monitors"}</_components.strong>{"."}</_components.li>{"\n"}<_components.li>{"Click the name of the monitor you want to specify an override for."}</_components.li>{"\n"}<_components.li>{"In the action menu, click the "}<Icon path={mdiDotsVertical} />{"\nthree vertical dots icon and select "}<_components.strong>{"Edit monitor"}</_components.strong>{". This opens a sidebar where\nyou can edit the monitor’s properties."}</_components.li>{"\n"}<_components.li>{"In the "}<_components.strong>{"Condition Override"}</_components.strong>{" section, click the plus icon "}<Icon path={mdiPlus} />{"\nto display the override fields."}</_components.li>{"\n"}<_components.li>{"Select "}<_components.strong>{"Exact"}</_components.strong>{" or "}<_components.strong>{"Regex"}</_components.strong>{" as the matcher type, and enter the key/value pair\nto match on for the override."}</_components.li>{"\n"}<_components.li>{"Select "}<_components.strong>{"Critical"}</_components.strong>{" or "}<_components.strong>{"Warn"}</_components.strong>{" as the override severity."}</_components.li>{"\n"}<_components.li>{"Define the match condition, and enter a value and sustain duration."}</_components.li>{"\n"}<_components.li>{"Click "}<_components.strong>{"Save"}</_components.strong>{" to apply the override changes."}</_components.li>{"\n"}</_components.ol></Tabs.Tab><Tabs.Tab label="Terraform" id="override-monitor-terraform"><_components.ol>{"\n"}<_components.li>{"\n"}<_components.p>{"Use "}<_components.a href="/tooling/infrastructure/terraform">{"Terraform"}</_components.a>{" to add an override\nto the resource’s existing properties and apply the changes."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Run this command to apply the changes:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"terraform"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" apply"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}</_components.ol><_components.p>{"The following YAML definition adds monitor overrides that match on specific labels,\nand associate either a "}<_components.code>{"warn"}</_components.code>{" or "}<_components.code>{"critical"}</_components.code>{" alert based on the matching key/value\npairs."}</_components.p><_components.pre tabIndex="0" data-language="yaml" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"series_conditions"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  defaults"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    critical"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"      conditions"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"        -"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" op"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" GT"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" value"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 20"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" sustain"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" 180s"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" resolve_sustain"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" 180s"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    warn"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"      conditions"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"        -"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" op"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" GT"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" value"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 10"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" sustain"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" 180s"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  overrides"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    -"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" label_matchers"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"        -"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" type"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" EXACT_MATCHER_TYPE"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" name"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" namespace"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" value"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" production"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"        -"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" type"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" EXACT_MATCHER_TYPE"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" name"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" container"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" value"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" k8s_prod"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"      severity_conditions"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"        critical"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"          conditions"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"            -"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" op"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" GT"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" value"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 50"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" sustain"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" 180s"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"        warn"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"          conditions"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"            -"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" op"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" GT"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" value"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 40"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" sustain"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" 180s"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"    -"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" label_matchers"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"        -"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" type"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" EXACT_MATCHER_TYPE"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" name"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" container"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" value"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" billing_svc"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"      severity_conditions"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"        critical"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"          conditions"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{" # only one of these conditions needs to be met."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"            -"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" op"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" GT"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" value"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 30"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"            -"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" op"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" GT"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" value"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 25"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{" sustain"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" 360s"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" }"}</_components.span></_components.span></_components.code></_components.pre><_components.p>{"Based on the previous override, the following descriptions explain how incoming\nseries impact alerting conditions for the monitor:"}</_components.p><_components.ul>{"\n"}<_components.li>{"\n"}<_components.p>{"The following series doesn’t match any override conditions, so the default alert\nconditions apply:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="yaml" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{"namespace=\"production\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" container=\"gateway\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" job=\"gateway-scraper\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 5"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"The following series matches the "}<_components.code>{"warn"}</_components.code>{" condition for the first override, so the\nalert uses a "}<_components.code>{"warn"}</_components.code>{" notifier:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="yaml" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{"namespace=\"production\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" container=\"k8s_prod\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" job=\"gateway-scraper\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 45"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"The following series matches the "}<_components.code>{"critical"}</_components.code>{" condition defined in the override, so the\nalert uses a "}<_components.code>{"critical"}</_components.code>{" notifier:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="yaml" data-word-wrap=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{"namespace=\"foo3\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" container=\"bar\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{","}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" job=\"gateway-scraper\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"}"}</_components.span><_components.span style={{
    "--shiki-dark": "#F5A97F",
    "--shiki-light": "#1976D2"
  }}>{" 27"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}</_components.ul></Tabs.Tab></Tabs>{"\n"}<_components.h2 id={toc[10].id}>{toc[10].value}</_components.h2>{"\n"}<_components.p>{"Select from the following methods to delete monitors."}</_components.p>{"\n"}<InfoTerraformLockout />{"\n"}<Tabs><Tabs.Tab label="Web" id="deleting-a-monitor-ui"><_components.p>{"To delete a monitor:"}</_components.p><_components.ol>{"\n"}<_components.li>{"In the navigation menu select\n"}<_components.strong><Icon path={mdiBell} />{" Alerts "}<span aria-label="and then">{">"}</span>{" Monitors"}</_components.strong>{"."}</_components.li>{"\n"}<_components.li>{"Click the name of the monitor you want to delete."}</_components.li>{"\n"}<_components.li>{"In the action menu, click the "}<Icon path={mdiDotsVertical} />{"\nthree vertical dots icon and select "}<_components.strong>{"Edit monitor"}</_components.strong>{"."}</_components.li>{"\n"}<_components.li>{"In the "}<_components.strong>{"Edit Monitor"}</_components.strong>{" dialog, click the "}<Icon path={mdiDotsVertical} />{" three\nvertical dots icon and select "}<_components.strong>{"Delete"}</_components.strong>{"."}</_components.li>{"\n"}</_components.ol></Tabs.Tab><Tabs.Tab label="Chronoctl" id="deleting-a-monitor-chronoctl"><_components.p>{"To delete a monitor with "}<_components.a href="/tooling/chronoctl">{"Chronoctl"}</_components.a>{", use the "}<_components.code>{"chronoctl monitors delete"}</_components.code>{"\ncommand:"}</_components.p><_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"chronoctl"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" monitors"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" delete"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" "}</_components.span><_components.mark data-highlighted-chars=""><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{"SLUG"}</_components.span></_components.mark></_components.span></_components.code></_components.pre><_components.p>{"Replace "}<_components.em><_components.code>{"SLUG"}</_components.code></_components.em>{" with the slug of the monitor you want to delete."}</_components.p><_components.p>{"For example, to delete a monitor with the slug "}<_components.code>{"infra-example-monitor"}</_components.code>{":"}</_components.p><_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"chronoctl"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" monitors"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" delete"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" infra-example-monitor"}</_components.span></_components.span></_components.code></_components.pre></Tabs.Tab><Tabs.Tab label="Terraform" id="deleting-a-monitor-terraform"><TerraformDel /></Tabs.Tab><Tabs.Tab label="API" id="deleting-a-monitor-api"><_components.p>{"To complete this action with the Chronosphere API, use the\n"}<_components.a href="/tooling/api-info/definition/operations/DeleteMonitor"><_components.code>{"DeleteMonitor"}</_components.code></_components.a>{" endpoint."}</_components.p><ApiAuthReq /></Tabs.Tab></Tabs>{"\n"}<_components.h2 id={toc[11].id}>{toc[11].value}</_components.h2>{"\n"}<_components.p>{"Create annotations for monitors that link to dashboards, runbooks, related documents,\nand "}<_components.a href="/investigate/querying/create-links/">{"trace metrics"}</_components.a>{",\nwhich lets you provide direct links for your on-call engineers to help diagnose\nissues."}</_components.p>{"\n"}<_components.p>{"You can reference Prometheus Alertmanager variables in annotations with the\n"}<_components.code>{"{{.VARIABLE_NAME }}"}</_components.code>{" syntax. Annotations can access monitor labels by using\nvariables with the "}<_components.code>{"{{ .CommonLabels.LABEL }}"}</_components.code>{" pattern, and from the alerting metric\nwith the "}<_components.code>{"{{ .Labels.LABEL }}"}</_components.code>{" pattern. In both patterns, replace "}<_components.em><_components.code>{"LABEL"}</_components.code></_components.em>{" with\nthe label’s name."}</_components.p>{"\n"}<Alert severity="info"><_components.p>{"To reference labels in Alertmanager variables, you must include those labels in the\nalerting time series. Otherwise, the resulting notifier won’t display any information\nfor the variables you specify."}</_components.p></Alert>{"\n"}<_components.p>{"The following examples include annotations with variables based on a template. See the\n"}<_components.a href="https://prometheus.io/docs/alerting/0.22/notifications/">{"Alertmanager documentation"}</_components.a>{"\nfor a reference list of alerting variables and templating functions."}</_components.p>{"\n"}<Tabs><Tabs.Tab label="Web" id="use-monitor-annotations-cloud"><_components.p>{"To add annotations to a monitor:"}</_components.p><_components.ol>{"\n"}<_components.li>{"\n"}<_components.p><_components.a href="#create-a-monitor">{"Create a monitor"}</_components.a>{"."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"In the "}<_components.strong>{"Annotations"}</_components.strong>{" section, add a description for your annotation in the\n"}<_components.strong>{"Key"}</_components.strong>{" field, and text or links in the "}<_components.strong>{"Value"}</_components.strong>{" field. For example, you might\nadd the following key/value pairs as annotations:"}</_components.p>{"\n"}<_components.table><_components.thead><_components.tr><_components.th>{"Key"}</_components.th><_components.th>{"Value"}</_components.th></_components.tr></_components.thead><_components.tbody><_components.tr><_components.td>{"summary"}</_components.td><_components.td>{"Instance "}<_components.code>{"{{$labels.instance}}"}</_components.code>{" is down"}</_components.td></_components.tr><_components.tr><_components.td>{"description"}</_components.td><_components.td>{"Container "}<_components.code>{"{{ $labels.namespace }}"}</_components.code>{"/"}<_components.code>{"{{ $labels.pod }}"}</_components.code>{"/"}<_components.code>{"{{ $labels.container }}"}</_components.code>{" terminated with "}<_components.code>{"{{ $labels.reason }}"}</_components.code>{"."}</_components.td></_components.tr><_components.tr><_components.td>{"runbook"}</_components.td><_components.td><_components.code>{"http://default-runbook"}</_components.code></_components.td></_components.tr></_components.tbody></_components.table>{"\n"}</_components.li>{"\n"}</_components.ol></Tabs.Tab><Tabs.Tab label="Chronoctl" id="use-monitor-annotations-chronoctl"><_components.p>{"To add annotations to a monitor:"}</_components.p><_components.ol>{"\n"}<_components.li>{"\n"}<_components.p>{"Define the resource definition for your monitor, and add an "}<_components.code>{"annotations"}</_components.code>{" section:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="yaml" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"api_version"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" v1/config"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"kind"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" Monitor"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"spec"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Required name of the monitor. Can be modified after the monitor is created."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  name"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" Instance is down"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # PromQL query. If set, you can't set graphite_query."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  prometheus_query"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" up{env=\"prod\", instance=\"kubernetes\", exported_job=\"JOB123\"} ==0"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Annotations are visible in notifications generated by this monitor."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # You can template annotations with labels from notifications."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"  annotations"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    summary"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" 'Instance {{$labels.instance}} is down'"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    description"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" 'Container {{ $labels.namespace }}/{{ $labels.pod }}/{{ $labels.container }} terminated with {{ $labels.reason }}.'"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-light": "#D32F2F"
  }}>{"    runbook"}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" 'http://default-runbook'"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Apply the changes:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"chronoctl"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" apply"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" -f"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" "}</_components.span><_components.mark data-highlighted-chars=""><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{"FILE_NAME"}</_components.span></_components.mark></_components.span></_components.code></_components.pre>{"\n"}<_components.p>{"Replace "}<_components.em><_components.code>{"FILE_NAME"}</_components.code></_components.em>{" with the name of your notifier YAML file."}</_components.p>{"\n"}</_components.li>{"\n"}</_components.ol></Tabs.Tab><Tabs.Tab label="Terraform" id="use-monitor-annotations-terraform"><_components.p>{"To add annotations to a monitor:"}</_components.p><_components.ol>{"\n"}<_components.li>{"\n"}<_components.p>{"Create a monitor with Terraform by using the "}<_components.code>{"chronosphere_monitor"}</_components.code>{" type followed\nby a name in a resource declaration:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="ruby" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"resource "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"chronosphere_monitor\""}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"instance_down\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{" {"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  name = "}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"\"Instance down\""}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Reference to the collection the alert belongs to"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  collection_id = chronosphere_collection"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"infra"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"id"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Override the notification policy."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # By default, uses the policy from the previously stated collection_id."}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  notification_policy_id = chronosphere_collection"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"infra_testing"}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{"."}</_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"id"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Arbitrary set of labels to assign to the alert"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  labels = "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"    \"priority\""}</_components.span><_components.span style={{
    "--shiki-dark": "#8BD5CA",
    "--shiki-light": "#D32F2F"
  }}>{" ="}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"sev-1\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span>{" "}</_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#6E738D",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#C2C3C5",
    "--shiki-light-font-style": "inherit"
  }}>{"  # Arbitrary set of annotations to include in alert notifications"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#CAD3F5",
    "--shiki-light": "#24292EFF"
  }}>{"  annotations = "}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"{"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"    \"summary\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"Instance {{$labels.instance}} is down\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"    \"description\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"Container {{ $labels.namespace }}/{{ $labels.pod }}/{{ $labels.container }} terminated with {{ $labels.reason }}.\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{"    \"runbook\""}</_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{":"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#22863A"
  }}>{" \"http://default-runbook\""}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#24292EFF"
  }}>{"  }"}</_components.span></_components.span>{"\n"}<_components.span><_components.span style={{
    "--shiki-dark": "#939AB7",
    "--shiki-light": "#212121"
  }}>{"..."}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"In the "}<_components.code>{"annotations"}</_components.code>{" section, define the annotations for your monitor."}</_components.p>{"\n"}</_components.li>{"\n"}<_components.li>{"\n"}<_components.p>{"Run "}<_components.code>{"terraform apply"}</_components.code>{" to create the monitor resource:"}</_components.p>{"\n"}<_components.pre tabIndex="0" data-language="shell" data-word-wrap="" data-copy=""><_components.code><_components.span><_components.span style={{
    "--shiki-dark": "#8AADF4",
    "--shiki-dark-font-style": "italic",
    "--shiki-light": "#6F42C1",
    "--shiki-light-font-style": "inherit"
  }}>{"terraform"}</_components.span><_components.span style={{
    "--shiki-dark": "#A6DA95",
    "--shiki-light": "#2B5581"
  }}>{" apply"}</_components.span></_components.span></_components.code></_components.pre>{"\n"}</_components.li>{"\n"}</_components.ol></Tabs.Tab></Tabs></>;
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}


export default HOC_MDXWrapper(
  MDXLayout,
  '/investigate/alerts/monitors',
  {"filePath":"pages/investigate/alerts/monitors.mdx",pageMap,frontMatter,title},
  typeof RemoteContent === 'undefined' ? useTOC : RemoteContent.useTOC
)